/**
 * OrizzontiPro → ValutaPro → PartenzaPro continuity token.
 *
 * `vg_journey_ref` stores {j: journey_id, t: signed_token} so the same B2C
 * journey can be picked up across tools without asking identity twice.
 *
 * IMPORTANT: this reference is transient. A journey completed months ago
 * MUST NOT hydrate identity into a brand-new ValutaPro analysis. A hard TTL
 * on the client side prevents old references from silently associating a
 * new analysis with a previous lead.
 *
 * Separation of concerns:
 *  - REFERRAL / provenance ........ cookie `vg_ref` (persistent, untouched)
 *  - IDENTITY / current journey ... this key `vg_journey_ref` (TTL-bounded)
 */
const KEY = "vg_journey_ref";
// 24 hours covers the same-day (or overnight) Orizzonti → Valuta → Partenza
// continuity while ensuring that a reference stored days/weeks/months ago
// is treated as stale and cleared.
export const JOURNEY_REF_TTL_MS = 24 * 60 * 60 * 1000;

export function saveJourneyRef({ j, t }) {
  if (!j || !t) return;
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({ j, t, savedAt: Date.now() })
    );
  } catch {
    /* quota */
  }
}

export function clearJourneyRef() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}

/**
 * Read the journey reference, enforcing the TTL. Returns null when:
 *  - the entry is missing / unparsable,
 *  - the entry lacks a numeric `savedAt` (legacy entry from before this fix),
 *  - the entry is older than the TTL.
 * In every one of those cases the stale entry is proactively removed so it
 * cannot re-hydrate a future analysis. Referral cookies are never touched.
 */
export function readJourneyRef({ maxAgeMs = JOURNEY_REF_TTL_MS } = {}) {
  let raw;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearJourneyRef();
    return null;
  }
  if (!parsed || typeof parsed !== "object" || !parsed.j || !parsed.t) {
    clearJourneyRef();
    return null;
  }
  const savedAt = typeof parsed.savedAt === "number" ? parsed.savedAt : null;
  if (savedAt === null) {
    // Legacy entry (pre-TTL) → treat as expired. Otherwise a reference from
    // a previous, unrelated analysis on this browser would keep hydrating
    // identity into every new ValutaPro session.
    clearJourneyRef();
    return null;
  }
  if (Date.now() - savedAt > maxAgeMs) {
    clearJourneyRef();
    return null;
  }
  return { j: parsed.j, t: parsed.t, savedAt };
}
